import { useRef, useEffect, useMemo } from 'react';
import { LoadingState, ErrorState } from '../ui';
import { MessageBubble, type Message, type ShadowMatch } from './MessageBubble';
import type { Proposal } from './ProposalBubble';

interface DialogueProps {
  messages: Message[];
  proposals?: Proposal[];
  isLoading: boolean;
  error?: Error | null;
  emptyMessage?: string;
  emptyIcon?: 'chat' | 'lightbulb';
  onRetryMessage?: ((messageId: string) => void) | undefined;
  onCorrectMessage?: (messageId: string, correctedText: string) => void;
}

export function Dialogue({
  messages,
  proposals = [],
  isLoading,
  error,
  emptyMessage = 'Пока нет сообщений',
  emptyIcon = 'chat',
  onRetryMessage,
  onCorrectMessage,
}: DialogueProps): JSX.Element {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Build map of shadow matches for operator messages
  // Match by finding the closest matched proposal before each operator message
  const shadowMatches = useMemo(() => {
    const matches = new Map<string, ShadowMatch>();

    const matchedProposals = proposals
      .filter(p => p.status === 'matched' && p.matchScore !== null && p.matchCategory)
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    // For each operator message, find the closest matched proposal before it
    const operatorMessages = messages.filter(m => m.isOutbound);

    for (const msg of operatorMessages) {
      const msgTime = new Date(msg.timestamp).getTime();

      // Find the last matched proposal that was created before this message
      // (within a reasonable window, e.g., 5 minutes)
      const relevantProposal = matchedProposals
        .filter(p => {
          const proposalTime = new Date(p.createdAt).getTime();
          const timeDiff = msgTime - proposalTime;
          // Proposal should be before the message and within 5 minutes
          return timeDiff >= 0 && timeDiff < 5 * 60 * 1000;
        })
        .pop(); // Get the last (most recent) one

      if (relevantProposal && relevantProposal.matchScore !== null && relevantProposal.matchCategory) {
        matches.set(msg.id, {
          score: relevantProposal.matchScore,
          category: relevantProposal.matchCategory,
        });
      }
    }

    return matches;
  }, [messages, proposals]);

  useEffect(() => {
    if (messages.length > 0 && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  if (isLoading && messages.length === 0) {
    return (
      <LoadingState />
    );
  }

  if (error) {
    return (
      <ErrorState error={error} />
    );
  }

  if (messages.length === 0) {
    const iconColor = emptyIcon === 'lightbulb' ? 'text-accent-ai/50' : 'text-accent-brand/50';
    return (
      <div className="flex flex-col items-center justify-center h-full text-text-secondary p-4">
        <div className="w-16 h-16 rounded-full bg-surface-subtle flex items-center justify-center mb-4">
          {emptyIcon === 'lightbulb' ? (
            <svg className={`w-8 h-8 ${iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          ) : (
            <svg className={`w-8 h-8 ${iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          )}
        </div>
        <p className="text-sm text-center">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      {messages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          shadowMatch={shadowMatches.get(message.id) ?? null}
          onRetry={onRetryMessage}
          {...(onCorrectMessage ? { onCorrect: onCorrectMessage } : {})}
        />
      ))}

      {/* Typing indicator — последнее сообщение от клиента, ответа ещё нет */}
      {messages.length > 0 && !messages[messages.length - 1]!.isOutbound && (
        <div className="flex items-center gap-2 pl-12">
          <div className="flex gap-1">
            <span className="w-2 h-2 rounded-full bg-accent-ai/40 animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-2 h-2 rounded-full bg-accent-ai/40 animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-2 h-2 rounded-full bg-accent-ai/40 animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
          <span className="text-xs text-text-muted">Обработка...</span>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
}
